// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The player's last-chosen hole-view basemap, mirrored locally so it survives closing the
// app.
//
// A player who picks "hazards" once — to see bunkers, water and the green over the imagery
// — is choosing it for every hole they will ever look at, not just the one in front of them:
// re-picking it on every hole, or losing it the next time the app is opened, defeats the
// point of it being a preference. So the last choice is cached here and rehydrated at boot,
// the same shape as {@link useDistanceUnit}.
//
// This is presentation only. It never gates what geometry is fetched or drawn — only which
// of the three already-available layers is shown by default — so a stale or missing value
// here can never hide data, only start a session on the wrong layer.

import { create } from 'zustand';

import { DEFAULT_BASEMAP, type HoleBasemap } from '@/features/hole/holeview/basemap';

const STORAGE_KEY = 'goffy:holeBasemap';

const isHoleBasemap = (value: string): value is HoleBasemap =>
  value === 'satellite' || value === 'hazards' || value === 'drawing';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readStored = async (): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(STORAGE_KEY);
  const local = getLocalStorage();
  if (local) return local.getItem(STORAGE_KEY);
  if (asyncStorage) return asyncStorage.getItem(STORAGE_KEY);
  return null;
};

const writeStored = async (value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(STORAGE_KEY, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(STORAGE_KEY, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(STORAGE_KEY, value);
};

type HoleBasemapState = {
  basemap: HoleBasemap;
  /** True once storage has been read, or once a choice has been made explicitly. */
  hydrated: boolean;
  setBasemap: (basemap: HoleBasemap) => void;
};

export const useHoleBasemapState = create<HoleBasemapState>((set) => ({
  basemap: DEFAULT_BASEMAP,
  hydrated: false,
  setBasemap: (basemap) => {
    set({ basemap, hydrated: true });
    void writeStored(basemap).catch(() => {
      // Losing the mirror only costs a moment on the wrong layer next cold start.
    });
  },
}));

/**
 * Reads the cached choice. Runs on import so no screen has to remember to call it.
 *
 * A choice already made explicitly wins over the read: a pick made before this settled must
 * not be clobbered by a slower read of what used to be there.
 */
export const hydrateHoleBasemap = async (): Promise<void> => {
  try {
    const stored = await readStored();
    if (stored === null || !isHoleBasemap(stored)) {
      useHoleBasemapState.setState({ hydrated: true });
      return;
    }

    if (useHoleBasemapState.getState().hydrated) return;
    useHoleBasemapState.setState({ basemap: stored, hydrated: true });
  } catch {
    useHoleBasemapState.setState({ hydrated: true });
  }
};

// A frame on the satellite default before the cached value lands is harmless — it is also
// where a player with no saved choice ends up anyway.
void hydrateHoleBasemap();

/** The hook the hole view uses. Re-renders when the preference changes. */
export const useHoleBasemap = (): HoleBasemap =>
  useHoleBasemapState((state) => state.basemap);

export const __setHoleBasemapStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __resetHoleBasemapForTests = () => {
  useHoleBasemapState.setState({ basemap: DEFAULT_BASEMAP, hydrated: false });
};
